import React, {
  useMemo,
  useState,
  useCallback,
  Dispatch,
  SetStateAction
} from 'react';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import SubdirectoryArrowLeftIcon from '@mui/icons-material/SubdirectoryArrowLeft';
import { MIN_SUPPORTED_DATE } from 'utils/date-time';
import { CalendarDateWithToday } from 'modules/common/components/CalendarDate/CalendarDateWithToday';
import { TimeSignal, TimeSignalAttribute } from 'graphql/types';
import { useMeContext } from 'modules/me';
import { TimeInput, NoteSourcesTable } from 'modules/notes/common';
import {
  AddNoteFormChangeHandlers,
  useSuggestionProps
} from '../addNote/hooks';
import { NOTE_FIELDS, NoteFormData } from '../common/noteSchema';
import { MergeDescription, DescriptionChangeProps } from './MergeDescription';

type StyleProps = {
  expanded: boolean;
  sourcesCount: number;
};

const noteDateLabel = <FormattedMessage id="note.startDate" />;

const useStyles = makeStyles((theme) => ({
  noteEditor: {
    backgroundColor: 'transparent',
    borderRadius: theme.spacing(0.5),
    padding: ({ expanded, sourcesCount }: StyleProps) =>
      theme.spacing(
        expanded ? 0 : 3,
        expanded ? 0 : 2.5,
        1,
        sourcesCount > 0 ? 2.5 : 0
      )
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  dateTime: {
    display: 'flex',
    padding: ({ sourcesCount }: StyleProps) =>
      theme.spacing(0, 2.5, 0, sourcesCount > 0 ? 2.5 : 0),
    marginTop: theme.spacing(-2)
  },
  noteDetails: {
    padding: theme.spacing(2, 3, 0, 3)
  },
  icon: {
    margin: theme.spacing(1, 2, 0, 2),
    transform: 'rotate(-90deg)'
  },
  mainContainer: {
    display: 'flex',
    paddingLeft: theme.spacing(2.5)
  },
  title: {
    display: 'flex',
    padding: theme.spacing(0.5, 2.5)
  },
  noteSourcesTable: {
    marginTop: ({ expanded }: StyleProps) => (!expanded ? theme.spacing(3) : 0)
  }
}));

const useDateStyles = makeStyles((theme) => ({
  inputField: {
    ...theme.typography.body1,
    color: theme.palette.text.primary
  }
}));

type MergeBodyProps = {
  errorMessage: (type: NOTE_FIELDS) => string | null | undefined;
  formChangeHandlers: AddNoteFormChangeHandlers;
  date: NoteFormData['date'];
  attributes?: TimeSignalAttribute[] | undefined;
  notes: TimeSignal[];
  primaryNoteId: string;
  setPrimaryNoteId: Dispatch<SetStateAction<string>>;
  expanded?: boolean;
};

export const MergeBody: React.FC<MergeBodyProps> = ({
  errorMessage,
  formChangeHandlers,
  date,
  attributes,
  notes,
  primaryNoteId,
  setPrimaryNoteId,
  expanded = false
}: MergeBodyProps) => {
  const suggestionProps = useSuggestionProps();
  const me = useMeContext();
  const primaryNote = useMemo(
    () => notes.find((note) => note.id === primaryNoteId),
    [notes, primaryNoteId]
  );
  const classes = useStyles({
    expanded,
    sourcesCount: primaryNote?.sources.length ?? 0
  });
  const dateStyles = useDateStyles();
  const [resetTimeValue, setResetTimeValue] = useState<boolean | undefined>(
    false
  );
  const calendarDate = useMemo(() => DateTime.fromJSDate(date as Date), [date]);
  const {
    hasSuggestionStartTime,
    hasSuggestionEndTime,
    hasSuggestionDuration
  } = suggestionProps;

  const onDescriptionChange = useCallback(
    ({ fetchSuggestions, description }: DescriptionChangeProps) => {
      setResetTimeValue(false);

      fetchSuggestions({
        attributes,
        description,
        hasSuggestionStartTime,
        hasSuggestionEndTime,
        hasSuggestionDuration
      });
    },
    [
      setResetTimeValue,
      attributes,
      hasSuggestionStartTime,
      hasSuggestionEndTime,
      hasSuggestionDuration
    ]
  );

  const onOptionChange = useCallback(
    (signal: TimeSignal) => {
      setPrimaryNoteId(signal.id);
    },
    [setPrimaryNoteId]
  );

  return (
    <>
      {expanded && (
        <div className={classes.title}>
          <Typography variant="subtitle1">
            <FormattedMessage id={'note.merge.toThis'} />
          </Typography>
          <SubdirectoryArrowLeftIcon
            color="action"
            fontSize={'medium'}
            className={classes.icon}
          />
        </div>
      )}
      <div className={classes.mainContainer}>
        {primaryNote?.sources && (
          <NoteSourcesTable
            className={classes.noteSourcesTable}
            sources={primaryNote?.sources}
          />
        )}

        <div className={classes.container}>
          <div className={classes.noteEditor}>
            <MergeDescription
              onDescriptionChange={onDescriptionChange}
              notes={notes}
              onOptionChange={onOptionChange}
            />
          </div>
          <div className={classes.dateTime}>
            <CalendarDateWithToday
              testId={'note'}
              hasEndAdornment
              value={calendarDate}
              inputVariant="outlined"
              minDate={MIN_SUPPORTED_DATE}
              fitContent
              disabled
              label={noteDateLabel}
              classes={dateStyles}
            />
            <TimeInput
              errorMessage={errorMessage}
              formChangeHandlers={formChangeHandlers}
              showStartEndTime={me?.showStartEndFields ?? false}
              resetTimeValue={resetTimeValue}
              setResetTimeValue={setResetTimeValue}
              suggestionProps={suggestionProps}
            />
          </div>
        </div>
      </div>
    </>
  );
};
